/**
 * TestSystemNotice.tsx — "you are on the TEST system" on the pharmacy sign-in
 * page. Renders on every non-prod host and NOTHING on the live one.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-09-11).
 *
 * WHY. On 2026-09-10 the STAGE sign-in page was opened twice, a few hours after
 * a live dispense email went out, and nothing was submitted — while the live
 * console had never been signed in to at all. A stale bookmark to the test
 * system fits: it lands with no link (so the page can only say "open the link
 * from your email"), and a link from a live email can never work here because
 * each environment keeps its own link tokens. The amber "Staging Environment"
 * ribbon (`StagingBadge` in ui/chrome) is already on this page, but it is
 * decorative by design — aria-hidden, sideways, at the screen edge — so it
 * orients a tester and tells a pharmacist nothing. This says it in words, under
 * the heading, with the way out.
 *
 * WHICH HOSTS: `isNonProdHost`, the one predicate the ribbon and the checkout
 * payment-stub note already use — never a second hostname check that could
 * drift from it. On the live host this returns null, so releasing it to prod
 * changes nothing there.
 *
 * THE LINK NEVER CARRIES A TOKEN. It is a constant pointing at the live sign-in
 * page. A token in this page's fragment belongs to THIS environment; forwarding
 * it would move a credential between systems for nothing, since it cannot sign
 * in to the live console.
 *
 * ACCESSIBILITY: `role="note"` with an accessible name, like the emergency
 * ribbon — static supplementary content that is there from first paint. Not a
 * live region (announcing it would only interrupt), and deliberately not
 * `alert`/`status`: this page keeps those for the sign-in outcome, and a second
 * alert would muddy the one message that matters. R28: no medicine is named.
 */
import { isNonProdHost } from '../ui/chrome';
import { Alert } from '../ui/kit';

/**
 * The LIVE pharmacy sign-in page. A constant, not config: it must name the same
 * live origin from every non-prod build, and the portal's origin settings are
 * per-environment by design. The spec pins that this can never point at a
 * non-prod host.
 */
export const LIVE_PHARMACY_SIGN_IN_URL = 'https://app.clinics.thegpservice.co.uk/auth-pharmacy';

/** What the link reads as — the address itself, so a pharmacist can check it against a bookmark. */
const LIVE_PHARMACY_SIGN_IN_LABEL = 'app.clinics.thegpservice.co.uk/auth-pharmacy';

export function TestSystemNotice() {
  if (!isNonProdHost()) return null;
  return (
    <div role="note" aria-labelledby="pharmacy-test-system-title" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <Alert tone="warning">
        <strong id="pharmacy-test-system-title">This is the GP Service test system.</strong> Orders for real
        patients never appear here, and a link from a live dispensing email will not work on this page. To
        dispense a real order, sign in on the live console at{' '}
        <a href={LIVE_PHARMACY_SIGN_IN_URL} style={{ color: 'inherit', fontWeight: 600 }}>
          {LIVE_PHARMACY_SIGN_IN_LABEL}
        </a>{' '}
        and update any bookmark that brought you here.
      </Alert>
    </div>
  );
}
